<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-ticket-portal">
      <template #header>
        <h3>Ticket Portal</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          The ticket portal lets customers access and reply to all of their prior email conversations. 
          They can sign in to the ticket portal with their own email address.
        </p>
        <template v-if="$permissions.ticketPortalFeatureEnabled">
          <div class="dd-divider" />
          <div class="toggler">
            <el-switch v-model="ticketPortalSettings.enabled" />
            <span>Ticket portal is <strong>{{ enabledText }}</strong> for this mailbox.</span>
          </div>
          <div v-if="ticketPortalSettings.enabled" class="mailbox-settings-ticket-portal__enabled-info">
            <h4>Link to Portal</h4>
            <p class="small">
              This is your generated link to your ticket portal.
              Anyone who has submitted an email to your mailbox can access their conversations from the ticket portal. 
            </p>
            <el-tooltip placement="top" content="Click to copy ticket portal link">
              <clipboard-text :text="ticketPortalSettings.signInUrl" class="ticket-portal-link" />
            </el-tooltip>
            <div class="dd-divider" />
            <h4>Ticket portal name</h4>
            <p class="small">
              Your mailbox name will appear on the header of the ticket portal. Override this with another name
              below&hellip;
            </p>
            <el-input v-model="ticketPortalSettings.ticketPortalName" maxlength="50" placeholder="e.g. Acme Support Ticket Portal" />
            <div class="dd-divider" />
            <h4>Customization</h4>
            <div class="toggler">
              <el-switch v-model="ticketPortalSettings.showRefNumber" />
              <p class="small"><strong>Show ticket number</strong> on listing and detail pages.</p>
            </div>
            <div class="toggler">
              <el-switch v-model="ticketPortalSettings.showStatuses" />
              <p class="small"><strong>Show statuses</strong> on listing and detail pages.</p>
            </div>
            <div class="toggler">
              <el-switch v-model="ticketPortalSettings.showAccountLogo" />
              <p class="small">
                <strong>Show account logo</strong> on sign-in page and portal header.
                <a
                  v-if="$permissions.isOwner"
                  :href="RouteAssistant.getAccountSettingsUrl($route.params.accountId)"
                  class="just-a-link"
                  target="_blank"
                  rel="noopener noreferrer nofollow">View/change account logo.</a></p>
            </div>
            <div class="dd-divider" />
            <dd-button
              :is-loading="isSubmitting"
              :color="ColorAssistant.MailboxColor"
              @click="save()"
            >Update ticket portal settings</dd-button>
          </div>
        </template>
        <template v-else>
          <p><br></p>
          <feature-gated-stamp />
        </template>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IMailboxesOutputTicketPortal } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsTicketPortal',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      clipboard: null as any,
      name: '',
      ticketPortalSettings: {} as IMailboxesOutputTicketPortal,
    }
  },

  head(): any {
    return {
      title: `Ticket Portal - ${this.name}`,
    }
  },
  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    enabledText(): string {
      return this.ticketPortalSettings.enabled ? 'enabled' : 'disabled'
    },
  },
  watch: {
    'ticketPortalSettings.enabled': {
      immediate: false,
      async handler(val: boolean) {
        if (!this.isLoading) {
          await this.save()
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    if (this.$permissions.ticketPortalFeatureEnabled) {
      await this.getTicketPortalSettings()
    } else {
      await this.getTitle()
    }
    this.isLoading = false
  },

  methods: {
    async getTicketPortalSettings() {
      try {
        const response = await MailboxesAPI.get_TicketPortal(this.id)
        this.name = response.name
        this.ticketPortalSettings = response
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getTitle() {
      try {
        const response = await MailboxesAPI.get_Title(this.id)
        this.name = response.name
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async save() {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.put_TicketPortal(this.id, {
          enabled: this.ticketPortalSettings.enabled,
          ticketPortalName: this.ticketPortalSettings.ticketPortalName,
          showRefNumber: this.ticketPortalSettings.showRefNumber,
          showStatuses: this.ticketPortalSettings.showStatuses,
          showAccountLogo: this.ticketPortalSettings.showAccountLogo,
        })
        await this.getTicketPortalSettings()
        this.isSubmitting = false
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error)
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-ticket-portal {
  .toggler {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    @include lettering--p;
  }

  &__enabled-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 30px 0 40px 0;
    border: solid 1px $color--gray-30;
    background-color: var(--color-background-primary);
    border-radius: 8px;
    padding: 20px;

    /* Override default clipboard-text class */
    .ticket-portal-link {
      @include lettering--p-sm;
      font-weight: $bold;
    }

    .el-input {
      margin: 10px 0;
    }

    .dd-button {
      width: fit-content;
      margin-left: auto;
    }
  }
}
</style>
